// SidebarMenu.js
import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkedAlt,
  faChartBar,
  faBoxesStacked,
  faUser,
  faCog,
  faSignOutAlt,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import logoWis from "../logo-wis.avif";

const SidebarMenu = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems = [
    {
      path: "/trafficControlTower",
      icon: faMapMarkedAlt,
      label: "CUS Control",
      description: "Traffic & Customs Management",
      color: "#4CAF50"
    },
    {
      path: "/customerItemCatalogueGrid",
      icon: faBoxesStacked,
      label: "Items Catalog",
      description: "Product & Inventory Management",
      color: "#2196F3"
    },
    {
      path: "/cusmxControlTowerDashboard",
      icon: faChartBar,
      label: "Dashboard",
      description: "Analytics & Reporting",
      color: "#FF9800"
    }
  ];

  const isActive = (path) => location.pathname === path;

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsExpanded(false);
      setHoveredItem(null);
    }, 300); // 300ms delay before collapsing
    setHoverTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  return (
    <div
      className="sidebar"
      style={{
        background: isExpanded
          ? "linear-gradient(135deg, #1A2654 0%, #1d4ed8 50%, #1A2654 100%)"
          : "linear-gradient(135deg, #1A2654, #1d4ed8)",
        width: isExpanded ? "280px" : "65px",
        height: "100vh",
        position: "fixed",
        zIndex: 1000,
        transition: "all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
        boxShadow: isExpanded 
          ? "8px 0 32px rgba(0,0,0,0.3), inset 1px 0 0 rgba(255,255,255,0.1)" 
          : "4px 0 16px rgba(0,0,0,0.2)",
        overflow: "hidden",
        cursor: isExpanded ? "default" : "pointer",
        backdropFilter: "blur(10px)",
        borderRight: "1px solid rgba(255,255,255,0.1)",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo Section */}
      <div 
        style={{
          padding: isExpanded ? "25px 20px" : "20px 8px",
          borderBottom: isExpanded ? "1px solid rgba(255,255,255,0.15)" : "none",
          textAlign: "center",
          transition: "all 0.4s ease-in-out",
        }}
      >
        {isExpanded ? (
          <div style={{
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? "scale(1)" : "scale(0.8)",
            transition: "all 0.4s ease-in-out 0.2s",
          }}>
            <img
              src={logoWis}
              alt="Wisdom Logo"
              style={{
                width: "140px",
                height: "auto",
                borderRadius: "12px",
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
                display: "block",
                margin: "0 auto 15px auto",
              }}
            />
            <div style={{
              color: "white",
              fontSize: "16px",
              fontWeight: "700",
              letterSpacing: "1px",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}>
              WISDOM CONNECT
            </div>
            <div style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: "11px",
              fontWeight: "400",
              letterSpacing: "0.5px",
              marginTop: "5px",
            }}>
              Enterprise Management Platform
            </div>
          </div>
        ) : (
          <div style={{
            width: "45px",
            height: "45px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.05))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
            transition: "all 0.3s ease-in-out",
            cursor: "pointer",
          }}>
            <FontAwesomeIcon 
              icon={faBars} 
              style={{
                color: "rgba(255,255,255,0.8)",
                fontSize: "18px",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />
          </div>
        )}
      </div>

      {/* Navigation Items */}
      <ul style={{ 
        listStyleType: "none", 
        padding: isExpanded ? "30px 0" : "20px 0",
        margin: 0,
        transition: "padding 0.3s ease-in-out",
      }}>
        {menuItems.map((item, index) => {
          const active = isActive(item.path);
          const hovered = hoveredItem === index;
          
          return (
            <li key={item.path} style={{ marginBottom: "8px" }}>
              <NavLink
                to={item.path}
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: isExpanded ? "60px" : "55px",
                  color: "white",
                  textDecoration: "none",
                  padding: isExpanded ? "0 25px" : "0",
                  margin: isExpanded ? "0 15px" : "0",
                  borderRadius: isExpanded ? "16px" : "0",
                  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  position: "relative",
                  background: active || hovered 
                    ? isExpanded 
                      ? "linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))"
                      : "rgba(255,255,255,0.1)"
                    : "transparent",
                  backdropFilter: (active || hovered) ? "blur(10px)" : "none",
                  border: (active || hovered) && isExpanded 
                    ? "1px solid rgba(255,255,255,0.2)" 
                    : "1px solid transparent",
                  transform: hovered && isExpanded ? "translateX(5px)" : "translateX(0)",
                }}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {/* Icon Container */}
                <div style={{
                  width: isExpanded ? "40px" : "45px",
                  height: isExpanded ? "40px" : "45px",
                  borderRadius: "12px",
                  background: active 
                    ? `linear-gradient(135deg, ${item.color}, ${item.color}cc)`
                    : hovered 
                      ? `linear-gradient(135deg, ${item.color}88, ${item.color}44)`
                      : "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: isExpanded ? "0 15px 0 0" : "0 auto",
                  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  transform: hovered ? "scale(1.1) rotate(5deg)" : "scale(1)",
                  boxShadow: active || hovered 
                    ? `0 8px 25px ${item.color}40, inset 0 1px 0 rgba(255,255,255,0.2)`
                    : "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}>
                  <FontAwesomeIcon
                    icon={item.icon}
                    size="lg"
                    style={{
                      color: active ? "white" : hovered ? "white" : "rgba(255,255,255,0.8)",
                      filter: active || hovered ? "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" : "none",
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                </div>

                {/* Label and Description */}
                {isExpanded && (
                  <div style={{
                    opacity: isExpanded ? 1 : 0,
                    transform: isExpanded ? "translateX(0)" : "translateX(-20px)",
                    transition: "all 0.4s ease-in-out 0.1s",
                  }}>
                    <div style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "2px",
                      color: active ? "white" : "rgba(255,255,255,0.9)",
                      textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}>
                      {item.label}
                    </div>
                    <div style={{
                      fontSize: "11px",
                      fontWeight: "400",
                      color: "rgba(255,255,255,0.6)",
                      lineHeight: "1.3",
                    }}>
                      {item.description}
                    </div>
                  </div>
                )}

                {/* Active Indicator */}
                {active && (
                  <div style={{
                    position: "absolute",
                    right: isExpanded ? "15px" : "8px",
                    width: "4px",
                    height: "20px",
                    background: `linear-gradient(to bottom, ${item.color}, ${item.color}88)`,
                    borderRadius: "2px",
                    boxShadow: `0 0 10px ${item.color}66`,
                  }} />
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>

      {/* User Section */}
      {isExpanded && (
        <div style={{
          position: "absolute",
          bottom: "20px",
          left: "15px",
          right: "15px",
          padding: "20px",
          background: "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.1)",
          backdropFilter: "blur(10px)",
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.4s ease-in-out 0.3s",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "12px",
          }}>
            <div style={{
              width: "35px",
              height: "35px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #4CAF50, #45a049)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
              boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
            }}>
              <FontAwesomeIcon icon={faUser} style={{ color: "white", fontSize: "14px" }} />
            </div>
            <div>
              <div style={{ color: "white", fontSize: "13px", fontWeight: "600" }}>
                Admin User
              </div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "10px" }}>
                System Administrator
              </div>
            </div>
          </div>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: "12px",
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}>
            <button style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              transition: "color 0.3s ease",
            }}>
              <FontAwesomeIcon icon={faCog} />
            </button>
            <button
              onClick={handleLogout}
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.7)",
                cursor: "pointer",
                transition: "color 0.3s ease",
              }}
            >
              <FontAwesomeIcon icon={faSignOutAlt} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarMenu;
